import Phaser from 'phaser';

export interface MembraneConfig {
  // Visual properties
  baseRadius: number;
  showBilayer: boolean;
  outerColor: number;
  innerColor: number;
  fillColor: number;
  outerAlpha: number;
  innerAlpha: number;
  fillAlpha: number;
  outerLineWidth: number;
  innerLineWidth: number;
  
  // Behavior properties
  wobbleAmplitude: number[];
  wobbleFrequency: number[];
  wobbleSpeed: number;
  
  // Feature flags
  showPhospholipids: boolean;
  allowBreaches: boolean;
  respondToMovement: boolean;
}

export class Membrane extends Phaser.GameObjects.Graphics {
  public baseRadius: number;
  private time: number = 0;
  private config: MembraneConfig;
  private waveSeeds: number[] = [];
  private outerPoints: { x: number, y: number, angle: number }[] = [];
  private innerPoints: { x: number, y: number, angle: number }[] = [];
  
  // Deformation properties (for movement response)
  private deformationAmount: number = 0;
  private deformationAngle: number = 0;
  private lastDeformationAngle: number = 0;
  private targetDeformationAmount: number = 0;

  // Breach properties
  private breaches: {angle: number, size: number, healingProgress: number}[] = [];
  
  // Add tint property
  private tintColor: number | null = null;
  
  // Add these properties while keeping existing ones
  private noiseOffsets: number[] = [
    Math.random() * 1000,
    Math.random() * 1000,
    Math.random() * 1000,
    Math.random() * 1000
  ];
  private lastTime: number = 0;
  private fluidMotion: { [key: number]: number } = {};
  
  // Default configuration
  private static readonly DEFAULT_CONFIG: MembraneConfig = {
    baseRadius: 100,
    showBilayer: true,
    outerColor: 0x4488FF,
    innerColor: 0x3377DD,
    fillColor: 0xAADDFF,
    outerAlpha: 1.0,
    innerAlpha: 1.0,
    fillAlpha: 0.5,
    outerLineWidth: 4,
    innerLineWidth: 3,
    
    wobbleAmplitude: [0.08, 0.05, 0.03],
    wobbleFrequency: [2, 4, 7],
    wobbleSpeed: 0.01,
    
    showPhospholipids: true,
    allowBreaches: false,
    respondToMovement: true
  };
  
  private readonly NETWORK_POINTS = 12; // Number of points to sync over network
  
  constructor(scene: Phaser.Scene, x: number, y: number, radius: number, config?: Partial<MembraneConfig>) {
    super(scene, { x, y });
    
    this.baseRadius = radius;
    
    // Merge provided config with defaults
    this.config = {
      ...Membrane.DEFAULT_CONFIG,
      baseRadius: radius, // Always use the provided radius
      ...config // Override with any provided config values
    };
    
    // Initialize wave parameters for organic movement
    this.waveSeeds = [Math.random() * 1000, Math.random() * 1000, Math.random() * 1000];
    
    // Add to scene
    scene.add.existing(this);
    
    // Initial draw
    this.redraw();
  }

  update(delta: number): void {
    // Normalize delta to ensure consistent animation regardless of frame rate
    const normalizedDelta = delta / 16.67;
    
    // Update animation time with frame rate compensation
    this.time += this.config.wobbleSpeed * normalizedDelta;
    
    // Handle deformation decay if using movement response
    if (this.config.respondToMovement) {
      if (this.targetDeformationAmount < 0.05) {
        this.deformationAmount *= Math.pow(0.95, normalizedDelta);
      }
    }
    
    // Handle breach healing
    if (this.config.allowBreaches && this.breaches.length > 0) {
      this.updateBreaches(normalizedDelta);
    }
    
    // Redraw every frame for smooth animation
    this.redraw();
  }
  
  // Method used by Cell.ts for movement deformation
  applyDeformation(amount: number, angle: number): void {
    // Don't apply deformation if this is a remote cell's membrane
    if (this.scene.scene.key === 'MultiplayerGameScene') {
      const isRemoteCell = (this.parentContainer as any) !== (this.scene as any).playerCell;
      if (isRemoteCell) {
        return;
      }
    }

    if (!this.config.respondToMovement) return;
    
    // Store target deformation for smoother transitions
    this.targetDeformationAmount = Math.min(amount / 10, 1.2);
    
    // Smooth deformation changes
    const smoothingFactor = 0.55;
    this.deformationAmount = this.deformationAmount * smoothingFactor + 
                            this.targetDeformationAmount * (1 - smoothingFactor);
    
    // Smooth angle changes
    if (this.targetDeformationAmount > 0.05) {
      if (this.lastDeformationAngle === undefined) {
        this.lastDeformationAngle = angle;
        this.deformationAngle = angle;
      } else {
        const angleDiff = this.normalizeAngle(angle - this.lastDeformationAngle);
        this.deformationAngle = this.normalizeAngle(this.lastDeformationAngle + angleDiff * 0.65);
        this.lastDeformationAngle = this.deformationAngle;
      }
    }
  }
  
  // Breach system methods
  createBreach(angle: number, size: number = 0.3): void {
    if (!this.config.allowBreaches) return;
    
    // Normalize angle to 0-2π range
    while (angle < 0) angle += Math.PI * 2;
    while (angle >= Math.PI * 2) angle -= Math.PI * 2;
    
    // Check if breach already exists at similar angle
    const existingBreachIndex = this.breaches.findIndex(
      breach => Math.abs(this.normalizeAngle(breach.angle - angle)) < size
    );
    
    if (existingBreachIndex >= 0) {
      // Expand existing breach
      const existingBreach = this.breaches[existingBreachIndex];
      existingBreach.size = Math.min(existingBreach.size + size, Math.PI / 2); // Max 90 degrees
      existingBreach.healingProgress = 0; // Reset healing
    } else {
      // Add new breach
      this.breaches.push({
        angle,
        size: Math.min(size, Math.PI / 2), // Max 90 degrees
        healingProgress: 0
      });
    }
    
    // Create particles at breach location if texture exists
    if (this.scene.textures.exists('particle')) {
      this.createBreachParticles(angle);
    }
  }
  
  private updateBreaches(normalizedDelta: number): void {
    // Process each breach
    for (let i = this.breaches.length - 1; i >= 0; i--) {
      const breach = this.breaches[i];
      
      // Advance healing progress
      breach.healingProgress += 0.0003 * normalizedDelta; // Very slow healing
      
      // Remove fully healed breaches
      if (breach.healingProgress >= 1) {
        this.breaches.splice(i, 1);
      }
    }
  }
  
  private createBreachParticles(angle: number): void {
    // Calculate position on membrane
    const x = Math.cos(angle) * this.baseRadius;
    const y = Math.sin(angle) * this.baseRadius;
    
    if (!this.scene.textures.exists('particle')) {
      return; // Skip if particle texture doesn't exist
    }
    
    // Create particles
    const particles = this.scene.add.particles(this.x + x, this.y + y, 'particle', {
      speed: { min: 50, max: 100 },
      scale: { start: 0.5, end: 0 },
      lifespan: 1000,
      quantity: 20,
      alpha: { start: 0.7, end: 0 },
      tint: [this.config.fillColor],
      emitting: false
    });
    
    // Emit once then destroy
    particles.explode(20);
    this.scene.time.delayedCall(1000, () => {
      particles.destroy();
    });
  }
  
  // Toggle phospholipids visibility
  togglePhospholipids(): void {
    this.config.showPhospholipids = !this.config.showPhospholipids;
    this.redraw();
  }
  
  // Add phospholipids (for compatibility with existing code)
  addPhospholipids(): void {
    this.config.showPhospholipids = true;
    this.redraw();
  }

  private redraw(): void {
    this.clear();
    
    // Store original colors
    const originalOuterColor = this.config.outerColor;
    const originalInnerColor = this.config.innerColor;
    const originalFillColor = this.config.fillColor;
    
    // Apply tint if specified
    if (this.tintColor !== null) {
      this.config.outerColor = this.tintColor;
      this.config.innerColor = this.tintColor;
      this.config.fillColor = this.tintColor;
    }
    
    // Draw bilayer if enabled, otherwise single membrane
    if (this.config.showBilayer) {
      this.drawBilayer();
    } else {
      this.drawSingleLayer();
    }
    
    // Draw phospholipids if enabled
    if (this.config.showPhospholipids) {
      this.drawPhospholipids();
    }
    
    // Draw breach effects if any exist
    if (this.config.allowBreaches && this.breaches.length > 0) {
      this.drawBreaches();
    }
    
    // Restore original colors
    if (this.tintColor !== null) {
      this.config.outerColor = originalOuterColor;
      this.config.innerColor = originalInnerColor;
      this.config.fillColor = originalFillColor;
    }
  }
  
  private drawSingleLayer(): void {
    // Draw outer membrane (single layer)
    this.lineStyle(this.config.outerLineWidth, this.config.outerColor, this.config.outerAlpha);
    this.fillStyle(this.config.fillColor, this.config.fillAlpha);
    
    this.beginPath();
    
    const points = 72; // Good balance between smoothness and performance
    
    for (let i = 0; i < points; i++) {
      const angle = (i / points) * Math.PI * 2;
      const radius = this.getRadius(angle);
      
      const x = radius * Math.cos(angle);
      const y = radius * Math.sin(angle);
      
      if (i === 0) {
        this.moveTo(x, y);
      } else {
        this.lineTo(x, y);
      }
    }
    
    this.closePath();
    this.fillPath();
    this.strokePath();
  }
  
  private drawBilayer(): void {
    // Draw outer membrane
    this.lineStyle(this.config.outerLineWidth, this.config.outerColor, this.config.outerAlpha);
    this.fillStyle(this.config.fillColor, this.config.fillAlpha);
    
    this.beginPath();
    
    const points = 90; // Higher point count for smoother curves
    this.outerPoints = []; // This is where the points are stored for network sync
    
    for (let i = 0; i < points; i++) {
      const angle = (i / points) * Math.PI * 2;
      const radius = this.getRadius(angle);
      
      const x = radius * Math.cos(angle);
      const y = radius * Math.sin(angle);
      
      this.outerPoints.push({ x, y, angle });
      
      if (i === 0) {
        this.moveTo(x, y);
      } else {
        this.lineTo(x, y);
      }
    }
    
    this.closePath();
    this.fillPath();
    this.strokePath();
    
    // Draw inner membrane
    this.lineStyle(this.config.innerLineWidth, this.config.innerColor, this.config.innerAlpha);
    
    this.beginPath();
    
    this.innerPoints = [];
    
    for (let i = 0; i < points; i++) {
      const angle = (i / points) * Math.PI * 2;
      const outerRadius = this.getRadius(angle);
      const radius = outerRadius * 0.85; // Inner membrane is 85% of outer radius
      
      const x = radius * Math.cos(angle);
      const y = radius * Math.sin(angle);
      
      this.innerPoints.push({ x, y, angle });
      
      if (i === 0) {
        this.moveTo(x, y);
      } else {
        this.lineTo(x, y);
      }
    }
    
    this.closePath();
    this.strokePath();
  }
  
  private drawPhospholipids(): void {
    // Check if we need to calculate points (in case we're using single layer)
    if (this.config.showBilayer) {
      if (this.outerPoints.length === 0 || this.innerPoints.length === 0) {
        this.calculatePhospholipidPoints();
      }
    } else {
      // For single layer, we only need outer points
      if (this.outerPoints.length === 0) {
        this.calculatePhospholipidPoints();
      }
    }
    
    // Draw phospholipids
    this.lineStyle(1.5, this.config.outerColor, 0.7);
    
    const lipidSpacing = 20; // Angle between lipids in degrees
    const lipidSpacingRad = lipidSpacing * Math.PI / 180;
    
    for (let angle = 0; angle < Math.PI * 2; angle += lipidSpacingRad) {
      // Find nearest points to this angle
      const outerIndex = Math.floor((angle / (Math.PI * 2)) * this.outerPoints.length);
      
      if (outerIndex >= this.outerPoints.length) continue;
      
      const outerPoint = this.outerPoints[outerIndex];
      
      // If bilayer, draw more complex phospholipids
      if (this.config.showBilayer) {
        this.drawBilayerPhospholipid(angle, outerIndex);
      } else {
        // Draw simple phospholipid for single layer
        this.drawSimplePhospholipid(outerPoint);
      }
    }
  }
  
  private drawSimplePhospholipid(outerPoint: {x: number, y: number, angle: number}): void {
    const angle = outerPoint.angle;
    
    // Draw phospholipid
    const x1 = outerPoint.x;
    const y1 = outerPoint.y;
    
    // Calculate inner position for lipid
    const x2 = x1 * 0.85;
    const y2 = y1 * 0.85;
    
    // Draw lipid line
    this.beginPath();
    this.moveTo(x1, y1);
    this.lineTo(x2, y2);
    this.strokePath();
    
    // Add small head to phospholipid
    this.fillStyle(this.config.outerColor, 0.5);
    this.fillCircle(x2, y2, 2);
  }
  
  private drawBilayerPhospholipid(angle: number, outerIndex: number): void {
    const outerPoint = this.outerPoints[outerIndex];
    const innerIndex = Math.floor((angle / (Math.PI * 2)) * this.innerPoints.length);
    
    if (innerIndex >= this.innerPoints.length) return;
    
    const innerPoint = this.innerPoints[innerIndex];
    
    // Draw outer phospholipid
    const outerAngle = outerPoint.angle;
    // Heads face outward
    const outerHeadX = outerPoint.x + Math.cos(outerAngle) * 4;
    const outerHeadY = outerPoint.y + Math.sin(outerAngle) * 4;
    // Tails point inward
    const outerTailX = outerPoint.x - Math.cos(outerAngle) * 14;
    const outerTailY = outerPoint.y - Math.sin(outerAngle) * 14;
    
    // Draw outer lipid
    this.lineStyle(1.5, this.config.outerColor, 0.7);
    this.beginPath();
    this.moveTo(outerHeadX, outerHeadY);
    this.lineTo(outerTailX, outerTailY);
    this.strokePath();
    
    // Draw outer phospholipid head
    this.fillStyle(this.config.outerColor, 0.6);
    this.fillCircle(outerHeadX, outerHeadY, 3);
    
    // Draw inner phospholipid
    const innerAngle = innerPoint.angle;
    // Heads face inward
    const innerHeadX = innerPoint.x - Math.cos(innerAngle) * 4;
    const innerHeadY = innerPoint.y - Math.sin(innerAngle) * 4;
    // Tails point outward
    const innerTailX = innerPoint.x + Math.cos(innerAngle) * 14;
    const innerTailY = innerPoint.y + Math.sin(innerAngle) * 14;
    
    // Draw inner lipid
    this.lineStyle(1.5, this.config.innerColor, 0.7);
    this.beginPath();
    this.moveTo(innerHeadX, innerHeadY);
    this.lineTo(innerTailX, innerTailY);
    this.strokePath();
    
    // Draw inner phospholipid head
    this.fillStyle(this.config.innerColor, 0.6);
    this.fillCircle(innerHeadX, innerHeadY, 3);
  }
  
  private calculatePhospholipidPoints(): void {
    // Calculate points for phospholipids if they weren't created during drawing
    const points = 90;
    this.outerPoints = [];
    this.innerPoints = [];
    
    for (let i = 0; i < points; i++) {
      const angle = (i / points) * Math.PI * 2;
      const outerRadius = this.getRadius(angle);
      
      const outerX = outerRadius * Math.cos(angle);
      const outerY = outerRadius * Math.sin(angle);
      
      this.outerPoints.push({ x: outerX, y: outerY, angle });
      
      if (this.config.showBilayer) {
        const innerRadius = outerRadius * 0.85;
        const innerX = innerRadius * Math.cos(angle);
        const innerY = innerRadius * Math.sin(angle);
        
        this.innerPoints.push({ x: innerX, y: innerY, angle });
      }
    }
  }
  
  private drawBreaches(): void {
    if (this.breaches.length === 0) return;
    
    // Draw breach visual effects
    this.breaches.forEach(breach => {
      // Draw a breach indicator
      const angle = breach.angle;
      const size = breach.size;
      const healingProgress = breach.healingProgress;
      
      // Calculate position on membrane
      const midAngle = angle;
      const radius = this.getRadius(midAngle);
      const x = Math.cos(midAngle) * radius;
      const y = Math.sin(midAngle) * radius;
      
      // Draw breach indicator
      this.fillStyle(0xff0000, 0.5 * (1 - healingProgress));
      this.fillCircle(x, y, 5 * (1 - healingProgress));
    });
  }
  
  private getRadius(angle: number): number {
    let radius = this.baseRadius;
    
    // Original wave deformation
    for (let w = 0; w < this.config.wobbleAmplitude.length; w++) {
      const amp = this.config.wobbleAmplitude[w];
      const freq = this.config.wobbleFrequency[w] || w + 2;
      const seed = this.waveSeeds[w];
      const time = this.time + seed;
      
      radius += Math.sin(angle * freq + time) * this.baseRadius * amp;
    }

    // Add fluid-like motion while keeping original behavior
    const currentTime = this.scene.time.now * 0.001;
    if (this.lastTime === 0) this.lastTime = currentTime;
    const deltaTime = currentTime - this.lastTime;
    
    // Create fluid-like deformation using multiple frequencies
    const angleKey = Math.floor(angle * 32 / (Math.PI * 2)); // Divide membrane into 32 sections
    if (!this.fluidMotion[angleKey]) {
      this.fluidMotion[angleKey] = 0;
    }

    // Update fluid motion
    const targetMotion = (
      Math.sin(angle * 2 + currentTime * 0.7 + this.noiseOffsets[0]) * 0.3 +
      Math.sin(angle * 3 + currentTime * 0.5 + this.noiseOffsets[1]) * 0.2 +
      Math.sin(angle * 5 + currentTime * 0.3 + this.noiseOffsets[2]) * 0.1
    ) * this.baseRadius * 0.1; // Scale factor for motion intensity

    // Smooth transition for fluid motion
    this.fluidMotion[angleKey] += (targetMotion - this.fluidMotion[angleKey]) * 
                                 Math.min(1, deltaTime * 3);

    // Apply fluid motion
    radius += this.fluidMotion[angleKey];

    // Apply movement deformation if enabled
    if (this.config.respondToMovement && this.deformationAmount > 0.01) {
      const angleDiff = this.normalizeAngle(angle - this.deformationAngle);
      
      // Primary stretch - in the direction of movement
      if (Math.abs(angleDiff) < Math.PI * 0.7) {
        const primaryStretch = Math.pow(Math.cos(angleDiff * 0.7), 2) * this.deformationAmount;
        radius += primaryStretch * this.baseRadius * 0.8;
      }
      
      // Secondary stretch - slight compression perpendicular to movement
      if (Math.abs(angleDiff) > Math.PI * 0.3 && Math.abs(angleDiff) < Math.PI) {
        const secondaryStretch = Math.sin(angleDiff) * this.deformationAmount * 0.2;
        radius -= Math.abs(secondaryStretch) * this.baseRadius * 0.1;
      }
      
      // Tertiary effect - slight compression at the back
      if (Math.abs(angleDiff) > Math.PI * 0.7) {
        const backEffect = Math.pow(Math.cos(angleDiff), 2) * this.deformationAmount * 0.3;
        radius -= backEffect * this.baseRadius * 0.15;
      }
    }
    
    // Apply breach effects
    if (this.config.allowBreaches && this.breaches.length > 0) {
      for (const breach of this.breaches) {
        const angleDiff = Math.abs(this.normalizeAngle(angle - breach.angle));
        
        if (angleDiff < breach.size) {
          // Create curve that's deepest at center of breach
          const breachDepth = (1 - (angleDiff / breach.size)) * 0.4; // 40% depth at maximum
          
          // Apply healing factor
          const healingFactor = breach.healingProgress;
          
          // Reduce radius based on breach depth and healing progress
          radius *= (1 - breachDepth * (1 - healingFactor));
        }
      }
    }

    this.lastTime = currentTime;
    return radius;
  }
  
  // Helper function to normalize angles to -PI to PI range
  private normalizeAngle(angle: number): number {
    while (angle > Math.PI) angle -= Math.PI * 2;
    while (angle < -Math.PI) angle += Math.PI * 2;
    return angle;
  }
  
  // For LivingMembrane compatibility
  public pulse(duration: number = 1000): void {
    // Create a pulsing effect
    this.scene.tweens.add({
      targets: this,
      scaleX: 1.1,
      scaleY: 1.1,
      duration: duration / 2,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        // Additional subtle pulse after the main one
        this.scene.tweens.add({
          targets: this,
          scaleX: 1.05,
          scaleY: 1.05,
          duration: duration / 4,
          yoyo: true,
          ease: 'Sine.easeInOut'
        });
      }
    });
  }

  // Add setTint method
  public setTint(color: number): this {
    this.tintColor = color;
    this.redraw();
    return this;
  }
  
  // Add clearTint method
  public clearTint(): this {
    this.tintColor = null;
    this.redraw();
    return this;
  }

  // Add a getter for breach count
  getBreachCount(): number {
    return this.breaches.length;
  }
  
  /**
   * Get the current membrane points for network synchronization
   */
  getPoints(): {x: number, y: number, angle: number}[] {
    // Create a smaller subset of points to transmit over the network
    // This significantly reduces bandwidth usage while still allowing for visual continuity
    const networkPoints: {x: number, y: number, angle: number}[] = [];
    
    const step = Math.floor(this.outerPoints.length / this.NETWORK_POINTS);
    
    for (let i = 0; i < this.NETWORK_POINTS; i++) {
      const index = (i * step) % this.outerPoints.length;
      networkPoints.push({...this.outerPoints[index]});
    }
    
    return networkPoints;
  }

  /**
   * Update the membrane path with points from the network
   */
  updateMembranePath(points: {x: number, y: number, angle: number}[]): void {
    // Safety check for the number of points
    if (!points || points.length < 3) {
      console.error('Not enough points to update membrane path', points);
      return;
    }
    
    // Store network points
    this.outerPoints = points;
    
    // Interpolate additional points for smooth rendering
    const smoothPoints: {x: number, y: number, angle: number}[] = [];
    const pointCount = 90; // Higher number for smooth rendering
    
    for (let i = 0; i < pointCount; i++) {
      const angle = (i / pointCount) * Math.PI * 2;
      // Find the two nearest network points
      const index1 = Math.floor((angle / (Math.PI * 2)) * points.length);
      const index2 = (index1 + 1) % points.length;
      const p1 = points[index1];
      const p2 = points[index2];
      
      // Interpolate between them
      const t = (angle - (index1 * Math.PI * 2 / points.length)) / 
               (Math.PI * 2 / points.length);
      const x = p1.x + (p2.x - p1.x) * t;
      const y = p1.y + (p2.y - p1.y) * t;
      
      smoothPoints.push({ x, y, angle });
    }
    
    // Use interpolated points for rendering
    this.outerPoints = smoothPoints;
    
    // Recalculate inner points
    this.innerPoints = this.outerPoints.map(point => ({
      x: point.x * 0.85,
      y: point.y * 0.85,
      angle: point.angle
    }));
    
    // Redraw with updated points
    this.redraw();
  }

  /**
   * Resizes the membrane to a new radius
   * @param newRadius The new radius for the membrane
   */
  public resize(newRadius: number): void {
    this.baseRadius = newRadius;
    this.config.baseRadius = newRadius;
    
    // Regenerate the membrane
    this.redraw();
    
    // Emit resize event
    this.emit('resized', newRadius);
  }
} 